// InvoiceModel.ts
import * as Yup from 'yup';

// Assuming BaseDTO doesn't have specific fields relevant to InvoiceDTO for this example.
// If it does, you'd include them here.
interface BaseDTO {
  // Example: createdAt?: Date; updatedAt?: Date;
}

export interface IInvoiceModel extends BaseDTO {
  // Cari / müşteri bilgileri
  id: string;
  customerId?: number | null;
  customerName?: string;
  phone?: string;
  address?: string;
  district?: string;
  city?: string;
  taxOffice?: string;
  taxNumber?: string;
  email?: string;
  branch?: string;

  // Fatura bilgileri
  invoiceDate?: Date | null;
  dueDate?: Date | null;
  documentNo?: string;
  returnInvoiceNo?: string;
  invoiceType?: string;
  eInvoiceType?: string;

  // Döviz bilgileri
  exchangeEur?: number | null;
  exchangeUsd?: number | null;
  exchangeGbp?: number | null;
  currency?: string;

  // Ödeme / gönderim bilgileri
  paymentType?: string;
  deliveryType?: string;
  kdvExemptionReason?: string;

  // Tutar bilgileri
  subtotal?: number | null;
  discountTotal?: number | null;
  amount?: number | null;
  kdv?: number | null;
  otv?: number | null;
  generalTotal?: number | null;
  foreignTotal?: number | null;

  // Tevkifat
  tevkifat?: number | null;
  tevkifatReason?: string;

  // E-fatura detayları
  eInvoiceTemplate?: string;
  invoiceIssuer?: string;
  invoiceIdText?: string;

  // Diğer bilgiler
  website?: string;
  description?: string;
}

// Define the shape of the invoice schema explicitly for Yup
export const invoiceSchemaShape = {
  // Cari / müşteri bilgileri
  customerId: Yup.number().nullable().optional(), // Adjusted to be optional and nullable
  customerName: Yup.string().nullable().optional(),
  phone: Yup.string().optional(), // Example: .matches(/^\+?[1-9]\d{1,14}$/, 'Geçerli bir telefon numarası giriniz.')
  address: Yup.string().optional(),
  district: Yup.string().optional(),
  city: Yup.string().optional(),
  taxOffice: Yup.string().optional(),
  taxNumber: Yup.string().optional(), // Example: .min(10, 'Vergi numarası en az 10 haneli olmalıdır.')
  email: Yup.string().email('Geçerli bir e-posta adresi giriniz.').optional(),
  branch: Yup.string().optional(),

  // Fatura bilgileri
  invoiceDate: Yup.date().required('Fatura tarihi zorunludur.').typeError('Geçerli bir tarih giriniz.'),
  dueDate: Yup.date().required('Vade tarihi zorunludur.').typeError('Geçerli bir tarih giriniz.'),
  documentNo: Yup.string().required('Belge numarası zorunludur.'),
  returnInvoiceNo: Yup.string().optional(),
  invoiceType: Yup.string().required('Fatura tipi zorunludur.'),
  eInvoiceType: Yup.string().optional(),

  // Döviz bilgileri
  exchangeEur: Yup.number().nullable().optional(),
  exchangeUsd: Yup.number().nullable().optional(),
  exchangeGbp: Yup.number().nullable().optional(),
  currency: Yup.string().optional(),

  // Ödeme / gönderim bilgileri
  paymentType: Yup.string().optional(),
  deliveryType: Yup.string().optional(),
  kdvExemptionReason: Yup.string().optional(),

  // Tutar bilgileri
  subtotal: Yup.number().nullable().optional(),
  discountTotal: Yup.number().nullable().optional(),
  amount: Yup.number().nullable().optional(),
  kdv: Yup.number().nullable().optional(),
  otv: Yup.number().nullable().optional(),
  generalTotal: Yup.number().nullable().optional(),
  foreignTotal: Yup.number().nullable().optional(),

  // Tevkifat
  tevkifat: Yup.number().nullable().optional(),
  tevkifatReason: Yup.string().optional(),

  // E-fatura detayları
  eInvoiceTemplate: Yup.string().optional(),
  invoiceIssuer: Yup.string().optional(),
  invoiceIdText: Yup.string().optional(),

  // Diğer bilgiler
  website: Yup.string().optional(),
  description: Yup.string().optional(),
};

export const initialInvoiceValues: IInvoiceModel = {
  id: '',               // kullanıldı
  // Cari / müşteri bilgileri
  customerId: null,   // kullanıldı
  customerName: '',   // kullanıldı
  phone: '',           //kullanıldı
  address: '',       // kullanıldı
  district: '',      // kullanıldı
  city: '',           // kullanıldı
  taxOffice: '',      // kullanıldı
  taxNumber: '',      // kullanıldı
  email: '',      // kullanıldı
  branch: '',     // kullanıldı

  // Fatura bilgileri
  invoiceDate: null,    // kullanıldı
  dueDate: null,    //kullanıldı
  documentNo: '',  // kullanıldı
  returnInvoiceNo: '',   //kullanıldı
  invoiceType: '',   // kullanıldı
  eInvoiceType: '',  // kullanıldı

  // Döviz bilgileri
  exchangeEur: null,   // kullanıldı
  exchangeUsd: null,   // kullanıldı
  exchangeGbp: null,   // kullanıldı
  currency: '',   // kullanıldı

  // Ödeme / gönderim bilgileri
  paymentType: '',   // kullanıldı
  deliveryType: '',   // kullanıldı
  kdvExemptionReason: '',   // kullanıldı

  // Tutar bilgileri
  subtotal: null,    
  discountTotal: null,
  amount: null,
  kdv: null,
  otv: null,
  generalTotal: null,
  foreignTotal: null,

  // Tevkifat
  tevkifat: null,
  tevkifatReason: '',

  // E-fatura detayları
  eInvoiceTemplate: '',
  invoiceIssuer: '',
  invoiceIdText: '',

  // Diğer bilgiler
  website: '',  // kullanıldı
  description: '',
};


export const invoiceSchema = Yup.object().shape(invoiceSchemaShape)

export const ECURRENCY_OPTIONS = [
  {value: 'USD', label: 'USD'},
  {value: 'TL', label: 'TL'},
  {value: 'EUR', label: 'EUR'},
  {value: 'DİĞERLERİ', label: 'DİĞERLERİ'},
] as const


export const PAYMENT_OPTIONS = [
  {value: 'Kart', label: 'Kart'},
  {value: 'Nakit', label: 'Nakit'},
  {value: 'Aylık', label: 'Aylık'},
  {value: 'DİĞERLERİ', label: 'DİĞERLERİ'},
] as const


export const DELIVERY_OPTIONS = [
  {value: 'Mail', label: 'Mail'},
  {value: 'Mesaj', label: 'Mesaj'},
  {value: 'Elden', label: 'Elden'},
  {value: 'DİĞERLERİ', label: 'DİĞERLERİ'},
] as const


export const KDVREASON_OPTIONS = [
  { value: 'istisna', label: 'KDV İstisnası' },        // Genel KDV istisnaları (ör: ihracat)
  { value: 'yeni_teknoloji', label: 'Yeni Teknoloji Ürünleri' }, // Bazı teşvik edilen ürünler
  { value: 'eğitim', label: 'Eğitim Hizmetleri' },    // Eğitim hizmetleri KDV’den muaf
  { value: 'sağlık', label: 'Sağlık Hizmetleri' },    // Sağlık hizmetleri KDV’den muaf
  { value: 'tarım', label: 'Tarım Ürünleri' },        // Bazı tarım ürünleri muaf
  { value: 'diger', label: 'Diğer' },                 // Kullanıcı kendi açıklamasını girebilir
] as const


export const EINVOICE_OPTIONS = [
  { value: 'ticari', label: 'Ticari Fatura' },          // Standart ticari fatura
  { value: 'serbest_meslek', label: 'Serbest Meslek Faturası' }, // Serbest meslek erbabı için
  { value: 'e_arsiv', label: 'E-Arşiv Fatura' },        // E-Arşiv fatura
  { value: 'diger', label: 'Diğer' },                  // Kullanıcının kendi açıklaması
] as const


export const TEVKIFATREASON_OPTIONS = [
  { value: 'ticari', label: 'Ticari Fatura (Standart)' },
  { value: 'serbest_meslek', label: 'Serbest Meslek Faturası' },
  { value: 'e_arsiv', label: 'E-Arşiv Fatura' },
  { value: 'diger', label: 'Diğer (Kullanıcı Açıklaması)' },
] as const
